import type { DepartmentLocation, NurseInShift } from "../../../utils/models";

/**
 * Find the department location that best matches a nurse's assignment.
 *
 * Priority chain (first match wins):
 * 1. **Exact match** — same location name, role allowed by `loc.roles` (or `"all"`), AND shift times match
 * 2. **Role match** — same location name AND role allowed
 * 3. **Shift-times match** — same location name AND shift times match
 * 4. **First-with-same-name fallback** — just pick the first location with the same name
 *
 * Returns `undefined` if no location shares the nurse's name (or if the nurse has no location).
 *
 * Used by Phase 1 (to attach `floor`/`section` to each ScheduleRow) and by role-group stats
 * (to find the "relevant locations" for a stat group).
 */
export const findDepartmentLocationForNurse = (nurse: NurseInShift, locations: DepartmentLocation[]): DepartmentLocation | undefined => {
	if (!nurse.location) return undefined;

	const locationsWithSameName = locations.filter((loc) => loc.location === nurse.location);
	if (locationsWithSameName.length === 0) return undefined;
	if (locationsWithSameName.length === 1) return locationsWithSameName[0];

	const nurseRole = nurse.role;
	const nurseStartTime = nurse.startTime?.toFormat("HH:mm");
	const nurseEndTime = nurse.endTime?.toFormat("HH:mm");

	const exactMatch = locationsWithSameName.find((loc) => {
		const roleMatches = loc.roles.includes("all") || (nurseRole && loc.roles.includes(nurseRole));
		const shiftTimesMatch = nurseStartTime === loc.shift_start && nurseEndTime === loc.shift_end;
		return roleMatches && shiftTimesMatch;
	});
	if (exactMatch) return exactMatch;

	const roleMatch = locationsWithSameName.find((loc) => loc.roles.includes("all") || (nurseRole && loc.roles.includes(nurseRole)));
	if (roleMatch) return roleMatch;

	const shiftTimesMatch = locationsWithSameName.find((loc) => nurseStartTime === loc.shift_start && nurseEndTime === loc.shift_end);
	if (shiftTimesMatch) return shiftTimesMatch;

	return locationsWithSameName[0];
};
